import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Card, money, Th, Td, DateRangeFilter, rangeParams } from "../components/shared";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  { v: "network_offer", l: "Network → Offer" },
  { v: "campaign", l: "Campaign" }, { v: "offer", l: "Offer" }, { v: "network", l: "Network" },
  { v: "traffic_source", l: "Traffic Source" }, { v: "country", l: "Country" }, { v: "device", l: "Device" },
  { v: "date", l: "Date" }, { v: "sub1", l: "Sub ID 1" }, { v: "email_id", l: "Email Subscriber" },
];

export default function Reports() {
  const [groupBy, setGroupBy] = useState("network_offer");
  const [rangeSel, setRangeSel] = useState({ range: "today" });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports?group_by=${groupBy}&${rangeParams(rangeSel)}`).then((r) => setData(r.data));
  }, [groupBy, rangeSel]);

  const exportCsv = () => {
    if (!data) return;
    const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [[GROUPS.find((g) => g.v === groupBy)?.l, "clicks", "unique", "conversions", "cr%", "revenue", "cost", "profit", "epc", "roi%"]];
    data.rows.forEach((r) => rows.push([r.key, r.clicks, r.unique_clicks, r.conversions, r.cr, r.revenue, r.cost, r.profit, r.epc, r.roi]));
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `omnilink_report_${groupBy}.csv`;
    a.click();
    toast.success("Report exported");
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports"
        subtitle="Multi-level grouping across your whole funnel"
        action={<Button data-testid="export-report-button" variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>}
      />
      <div className="flex flex-wrap gap-3 mb-4 fade-up">
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger data-testid="report-groupby-select" className="w-52 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{GROUPS.map((g) => <SelectItem key={g.v} value={g.v}>Group by: {g.l}</SelectItem>)}</SelectContent>
        </Select>
        <DateRangeFilter value={rangeSel} onChange={setRangeSel} />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <Th>{GROUPS.find((g) => g.v === groupBy)?.l}</Th>
                <Th className="text-right">Clicks</Th><Th className="text-right">Unique</Th><Th className="text-right">Conv.</Th>
                <Th className="text-right">CR %</Th><Th className="text-right">Revenue</Th><Th className="text-right">Cost</Th>
                <Th className="text-right">Profit</Th><Th className="text-right">EPC</Th><Th className="text-right">ROI %</Th>
              </tr>
            </thead>
            <tbody data-testid="report-table-body">
              {data?.rows.map((r) => (
                <tr key={r.key} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-900">{r.key}</Td>
                  <Td className="text-right font-mono">{r.clicks.toLocaleString()}</Td>
                  <Td className="text-right font-mono">{r.unique_clicks.toLocaleString()}</Td>
                  <Td className="text-right font-mono">{r.conversions}</Td>
                  <Td className="text-right font-mono">{r.cr}%</Td>
                  <Td className="text-right font-mono font-semibold text-emerald-600">{money(r.revenue)}</Td>
                  <Td className="text-right font-mono text-slate-500">{money(r.cost)}</Td>
                  <Td className={`text-right font-mono font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(r.profit)}</Td>
                  <Td className="text-right font-mono">${r.epc}</Td>
                  <Td className={`text-right font-mono ${r.roi >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.roi}%</Td>
                </tr>
              ))}
            </tbody>
            {data && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50/80 font-semibold">
                  <Td className="text-slate-900 font-semibold">TOTALS ({data.rows.length} rows)</Td>
                  <Td className="text-right font-mono">{data.totals.clicks.toLocaleString()}</Td>
                  <Td />
                  <Td className="text-right font-mono">{data.totals.conversions}</Td>
                  <Td />
                  <Td className="text-right font-mono text-emerald-700">{money(data.totals.revenue)}</Td>
                  <Td className="text-right font-mono">{money(data.totals.cost)}</Td>
                  <Td className="text-right font-mono text-emerald-700">{money(data.totals.profit)}</Td>
                  <Td /><Td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {!data && <div className="p-8 text-center text-sm text-slate-400 animate-pulse">Building report…</div>}
      </Card>
    </div>
  );
}
